import { useTranslation } from "react-i18next";
import { DowntimeMethodCapturesEntity } from "entities/DowntimeMethodCaptures/DowntimeMethodCapturesEntity";
import { Formik, Form } from "formik";
import {
    Box,
    Button,
    Grid,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Select,
    Switch,
    Typography,
    TextField,
    InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { IMachineCenterCreationFormValues, IMachineCenterCreationForm } from "./MachineCenterCreationFormInterfaces";
import { machineCenterCorporateBOFormSchema } from "validations/schemas";
import { useStyles } from "./MachineCenterCreationForm.styles";
import { DOWNTIMEMETHODCAPTURE } from "constants/constants";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { useCallback, useMemo, useState } from "react";
import { onlyNumbersRegex } from "validations/matchers";

export const MachineCenterCreationForm = ({
    handleSubmit,
    handleClose,
    apolloMutationError,
    isEdit,
    machineCenter,
    millId,
    machineCenterId,
    duplicateMachineCenterInfo,
}: IMachineCenterCreationForm) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const isDuplicationWithSelection = useMemo(() => Boolean(duplicateMachineCenterInfo), [duplicateMachineCenterInfo]);
    const [errorUniquePlcAddress, setErrorUniquePlcAddress] = useState("");
    const [machineCenterIdSelected, setMachineCenterIdSelected] = useState<number | undefined>(undefined);
    const [duplicationInCreation, setDuplicationInCreation] = useState<boolean>(false);

    const { downtimeMethodCaptures } = DowntimeMethodCapturesEntity.GetDowntimeMethodCapture();
    const {
        machineCenterFailureDetails,
        productionLineOptions,
        loading,
        error: errorMachineCenterDuplicationDetails,
    } = MachineCentersEntity.GetMachineCenterFailureDetailsInfoBO(
        duplicateMachineCenterInfo?.machineCenterId || machineCenterIdSelected,
        duplicationInCreation ? millId : undefined,
    );

    const handleSubmitCallback = useCallback(
        async (values: IMachineCenterCreationFormValues) => {
            let checkUniquenessPlcAddress = values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC;
            if (checkUniquenessPlcAddress) {
                const uniquePlcAddress = await MachineCentersEntity.uniquePlcAddressByMill(
                    millId,
                    values.plc_address,
                    machineCenterId,
                );
                if (uniquePlcAddress.unique) {
                    setErrorUniquePlcAddress("");
                    await handleSubmit(values);
                } else {
                    setErrorUniquePlcAddress(
                        t(
                            "corporate_backoffice.production_line_view.machine_center_creation_form.unique_plc_address_error_message",
                            { name: uniquePlcAddress.machineCenterName },
                        ),
                    );
                }
            } else {
                if ((isDuplicationWithSelection || duplicationInCreation) && machineCenterFailureDetails[0]) {
                    await handleSubmit(values, machineCenterFailureDetails[0]);
                } else {
                    await handleSubmit(values);
                }
            }
        },
        [
            handleSubmit,
            machineCenterId,
            millId,
            t,
            machineCenterFailureDetails,
            isDuplicationWithSelection,
            duplicationInCreation,
        ],
    );

    const errorMessage = useMemo(() => {
        if (apolloMutationError?.message.includes("uq_machine_center_name")) {
            return t(
                "corporate_backoffice.production_line_view.machine_center_creation_form.unique_name_machine_center_name",
            );
        } else if (Boolean(errorUniquePlcAddress)) {
            return errorUniquePlcAddress;
        } else if (apolloMutationError?.message.includes("uq_machine_center_plc_address_by_mill")) {
            return t(
                "corporate_backoffice.production_line_view.machine_center_creation_form.unique_plc_address_error_message_fixed",
            );
        } else if (Boolean(errorMachineCenterDuplicationDetails)) {
            return t(
                "corporate_backoffice.production_line_view.machine_center_creation_form.get_duplication_details_error",
            );
        } else {
            return t("setup_corporation.errors.creation_error");
        }
    }, [errorUniquePlcAddress, apolloMutationError, errorMachineCenterDuplicationDetails, t]);

    const initFormValues: IMachineCenterCreationFormValues = useMemo(
        () =>
            isEdit && machineCenter
                ? machineCenter
                : {
                      name: "",
                      short_name: "",
                      manufacturer: "",
                      model_number: "",
                      downtime_method_capture_id: "",
                      plc_address: "",
                      minimum_downtime: "",
                      downtime_trigger_threshold: "",
                      is_bottleneck: false,
                      is_duplication_with_selection: Boolean(isDuplicationWithSelection),
                      is_duplication_without_selection: duplicationInCreation,
                      production_line_id_send_duplicate: duplicateMachineCenterInfo?.productionLineId || "",
                      machine_center_id_to_duplicate: "",
                      production_line_id_to_duplicate: "",
                  },
        [duplicateMachineCenterInfo, isDuplicationWithSelection, duplicationInCreation, isEdit, machineCenter],
    );

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={machineCenterCorporateBOFormSchema}
            onSubmit={handleSubmitCallback}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="outlined-name-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.production_line_view.machine_center_creation_form.short_name",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="outlined-short-name-label"
                                        fullWidth
                                        id="outlined-short-name-input"
                                        className={classes.textField}
                                        name="short_name"
                                        type="text"
                                        variant="outlined"
                                        value={values.short_name}
                                        onChange={handleChange}
                                        error={touched.short_name && Boolean(errors.short_name)}
                                        helperText={touched.short_name && errors.short_name && t(errors.short_name)}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={8}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="outlined-name-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.production_line_view.machine_center_creation_form.name",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="outlined-name-label"
                                        fullWidth
                                        id="outlined-name-input"
                                        className={classes.textField}
                                        name="name"
                                        type="text"
                                        variant="outlined"
                                        value={values.name}
                                        onChange={handleChange}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name && t(errors.name)}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        {isDuplicationWithSelection && !isEdit && (
                            <Box className={classes.boxContainer}>
                                <InputLabel id="outlined-name-label">
                                    <Typography variant="subtitle2" className={classes.formLabel}>
                                        {t(
                                            "corporate_backoffice.production_line_view.machine_center_creation_form.production_line",
                                        )}
                                    </Typography>
                                </InputLabel>
                                <FormControl
                                    error={
                                        touched.production_line_id_send_duplicate &&
                                        Boolean(errors.production_line_id_send_duplicate)
                                    }
                                    className={classes.textField}
                                    fullWidth
                                >
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        className={classes.select}
                                        name="production_line_id_send_duplicate"
                                        value={values.production_line_id_send_duplicate}
                                        onChange={handleChange}
                                    >
                                        {duplicateMachineCenterInfo?.productionLineOptions.map((pl) => (
                                            <MenuItem
                                                key={pl.id}
                                                value={pl.id}
                                                onClick={() => {
                                                    setFieldValue("production_line_id_send_duplicate", pl.id);
                                                }}
                                            >
                                                {pl.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {touched.production_line_id_send_duplicate &&
                                            errors.production_line_id_send_duplicate &&
                                            t(errors.production_line_id_send_duplicate)}
                                    </FormHelperText>
                                </FormControl>
                            </Box>
                        )}
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.manufacturer",
                                    )}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-manufacturer-label"
                                fullWidth
                                id="outlined-manufacturer-input"
                                className={classes.textField}
                                name="manufacturer"
                                type="text"
                                variant="outlined"
                                value={values.manufacturer}
                                onChange={handleChange}
                                error={touched.manufacturer && Boolean(errors.manufacturer)}
                                helperText={touched.manufacturer && errors.manufacturer && t(errors.manufacturer)}
                            />
                        </Box>
                        <Box className={classes.boxContainerSeparator}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.model_number",
                                    )}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-model-number-label"
                                fullWidth
                                id="outlined-model-number-input"
                                className={classes.textField}
                                name="model_number"
                                type="text"
                                variant="outlined"
                                value={values.model_number}
                                onChange={handleChange}
                                error={touched.model_number && Boolean(errors.model_number)}
                                helperText={touched.model_number && errors.model_number && t(errors.model_number)}
                            />
                        </Box>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_method_capture",
                                    )}
                                </Typography>
                            </InputLabel>
                            <FormControl
                                error={touched.downtime_method_capture_id && Boolean(errors.downtime_method_capture_id)}
                                className={classes.textField}
                                fullWidth
                            >
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    className={classes.select}
                                    name="downtime_method_capture_id"
                                    value={values.downtime_method_capture_id}
                                    onChange={handleChange}
                                >
                                    {downtimeMethodCaptures.map((downtime_method_capture, index) => (
                                        <MenuItem
                                            key={index}
                                            value={downtime_method_capture.downtime_method_capture_id}
                                            onClick={() => {
                                                setFieldValue(
                                                    "downtime_method_capture_id",
                                                    downtime_method_capture.downtime_method_capture_id,
                                                );
                                            }}
                                        >
                                            {downtime_method_capture.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {touched.downtime_method_capture_id &&
                                        errors.downtime_method_capture_id &&
                                        t(errors.downtime_method_capture_id)}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t(
                                        values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
                                            ? "corporate_backoffice.production_line_view.machine_center_creation_form.plc_address_required"
                                            : "corporate_backoffice.production_line_view.machine_center_creation_form.plc_address",
                                    )}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-plc-address-label"
                                fullWidth
                                id="outlined-plc-address-input"
                                className={classes.textField}
                                name="plc_address"
                                disabled={!(values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC)}
                                type="text"
                                variant="outlined"
                                value={
                                    values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
                                        ? values.plc_address
                                        : ""
                                }
                                onChange={handleChange}
                                error={touched.plc_address && Boolean(errors.plc_address)}
                                helperText={touched.plc_address && errors.plc_address && t(errors.plc_address)}
                            />
                        </Box>
                        <Grid className={classes.boxContainerSeparator} container spacing={2}>
                            <Grid item xs={6}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="outlined-minimum-downtime-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.production_line_view.machine_center_creation_form.minimum_downtime_definition",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="outlined-minimum-downtime-label"
                                        fullWidth
                                        id="outlined-minimum-downtime-input"
                                        className={classes.textField}
                                        placeholder={t(
                                            "corporate_backoffice.production_line_view.machine_center_creation_form.minimum_downtime_definition_placeholder",
                                        )}
                                        name="minimum_downtime"
                                        type="text"
                                        variant="outlined"
                                        value={values.minimum_downtime}
                                        onChange={(e) => {
                                            if (e.target.value === "" || onlyNumbersRegex.test(e.target.value)) {
                                                handleChange(e);
                                            }
                                        }}
                                        error={touched.minimum_downtime && Boolean(errors.minimum_downtime)}
                                        helperText={
                                            touched.minimum_downtime &&
                                            errors.minimum_downtime &&
                                            t(errors.minimum_downtime)
                                        }
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box className={classes.boxContainer}>
                                    <InputLabel id="outlined-downtime-trigger-threshold-label">
                                        <Typography variant="subtitle2" className={classes.formLabel}>
                                            {t(
                                                "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_trigger",
                                            )}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        aria-labelledby="outlined-downtime-trigger-threshold-label"
                                        fullWidth
                                        id="outlined-downtime-trigger-threshold-input"
                                        className={classes.textField}
                                        placeholder={t(
                                            "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_trigger_placeholder",
                                        )}
                                        name="downtime_trigger_threshold"
                                        type="text"
                                        variant="outlined"
                                        value={values.downtime_trigger_threshold}
                                        onChange={(e) => {
                                            if (e.target.value === "" || onlyNumbersRegex.test(e.target.value)) {
                                                handleChange(e);
                                            }
                                        }}
                                        error={
                                            touched.downtime_trigger_threshold &&
                                            Boolean(errors.downtime_trigger_threshold)
                                        }
                                        helperText={
                                            touched.downtime_trigger_threshold &&
                                            errors.downtime_trigger_threshold &&
                                            t(errors.downtime_trigger_threshold)
                                        }
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <FormControlLabel
                            className={classes.switchLabel}
                            value="start"
                            control={
                                <Switch
                                    className={classes.switch}
                                    checked={values.is_bottleneck}
                                    onChange={(e) => setFieldValue("is_bottleneck", e.target.checked)}
                                />
                            }
                            label={t(
                                "corporate_backoffice.production_line_view.machine_center_creation_form.bottleneck",
                            )}
                            labelPlacement="start"
                        />
                        {!isDuplicationWithSelection && !isEdit && (
                            <FormControlLabel
                                className={classes.switchLabel}
                                value="start"
                                control={
                                    <Switch
                                        className={classes.switch}
                                        checked={values.is_duplication_without_selection}
                                        onChange={(e) => {
                                            setDuplicationInCreation(e.target.checked);
                                            setFieldValue("is_duplication_without_selection", e.target.checked);
                                        }}
                                    />
                                }
                                label={t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.duplication",
                                )}
                                labelPlacement="start"
                            />
                        )}
                        {!isDuplicationWithSelection && !isEdit && duplicationInCreation && (
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box className={classes.boxContainer}>
                                        <InputLabel id="outlined-name-label">
                                            <Typography variant="subtitle2" className={classes.formLabel}>
                                                {t(
                                                    "corporate_backoffice.production_line_view.machine_center_creation_form.production_line",
                                                )}
                                            </Typography>
                                        </InputLabel>
                                        <FormControl
                                            error={
                                                touched.production_line_id_to_duplicate &&
                                                Boolean(errors.production_line_id_to_duplicate)
                                            }
                                            className={classes.textField}
                                            fullWidth
                                        >
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                className={classes.select}
                                                name="production_line_id_to_duplicate"
                                                value={values.production_line_id_to_duplicate}
                                                onChange={handleChange}
                                            >
                                                {productionLineOptions.map((pl) => (
                                                    <MenuItem
                                                        key={pl.production_line_id}
                                                        value={pl.production_line_id}
                                                        onClick={() => {
                                                            setFieldValue(
                                                                "production_line_id_to_duplicate",
                                                                pl.production_line_id,
                                                            );
                                                        }}
                                                    >
                                                        {pl.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.production_line_id_to_duplicate &&
                                                    errors.production_line_id_to_duplicate &&
                                                    t(errors.production_line_id_to_duplicate)}
                                            </FormHelperText>
                                        </FormControl>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.boxContainer}>
                                        <InputLabel id="outlined-name-label">
                                            <Typography variant="subtitle2" className={classes.formLabel}>
                                                {t(
                                                    "corporate_backoffice.production_line_view.machine_center_creation_form.machine_center",
                                                )}
                                            </Typography>
                                        </InputLabel>
                                        <FormControl
                                            error={
                                                touched.machine_center_id_to_duplicate &&
                                                Boolean(errors.machine_center_id_to_duplicate)
                                            }
                                            className={classes.textField}
                                            fullWidth
                                        >
                                            <Select
                                                disabled={!values.production_line_id_to_duplicate}
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                className={classes.select}
                                                name="machine_center_id_to_duplicate"
                                                value={values.machine_center_id_to_duplicate}
                                                onChange={handleChange}
                                            >
                                                {productionLineOptions
                                                    .find(
                                                        (pl) =>
                                                            pl.production_line_id ===
                                                            values.production_line_id_to_duplicate,
                                                    )
                                                    ?.machine_centers?.map((mc) => (
                                                        <MenuItem
                                                            key={mc.machine_center_id}
                                                            value={mc.machine_center_id}
                                                            onClick={() => {
                                                                setMachineCenterIdSelected(mc.machine_center_id);
                                                                setFieldValue(
                                                                    "machine_center_id_to_duplicate",
                                                                    mc.machine_center_id,
                                                                );
                                                            }}
                                                        >
                                                            {mc.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.machine_center_id_to_duplicate &&
                                                    errors.machine_center_id_to_duplicate &&
                                                    t(errors.machine_center_id_to_duplicate)}
                                            </FormHelperText>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                        <p
                            style={{
                                display:
                                    apolloMutationError ||
                                    Boolean(errorUniquePlcAddress) ||
                                    Boolean(errorMachineCenterDuplicationDetails)
                                        ? "block"
                                        : "none",
                                color: "red",
                            }}
                        >
                            {errorMessage}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.cancelButton",
                                )}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting || loading || Boolean(errorMachineCenterDuplicationDetails)}
                            >
                                {isEdit ? (
                                    t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.edit_button",
                                    )
                                ) : (
                                    <>
                                        <AddIcon />{" "}
                                        {t(
                                            "corporate_backoffice.production_line_view.machine_center_creation_form.addButton",
                                        )}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
